import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notifyError } from '../notify';
import ScraperAccounts from '../components/ScraperAccounts';
import SubFinder from '../components/SubFinder';
import FoundSubs from '../components/FoundSubs';
import TagFilter from '../components/TagFilter';


// Auto-saving input
function AutoSaveInput({ value, onSave, placeholder, disabled, multiline, ...props }) {
  const [localValue, setLocalValue] = useState(value || '');
  const timerRef = useRef(null);
  const savedRef = useRef(value || '');

  const save = useCallback((val) => {
    if (val !== savedRef.current) {
      savedRef.current = val;
      onSave(val);
    }
  }, [onSave]);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(e.target.value), 1000);
  };

  const handleBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    save(localValue);
  };

  useEffect(() => {
    setLocalValue(value || '');
    savedRef.current = value || '';
  }, [value]);

  if (multiline) {
    return <textarea value={localValue} onChange={handleChange} onBlur={handleBlur} placeholder={placeholder} disabled={disabled} rows={3} style={{ width: '100%' }} {...props} />;
  }
  return <input value={localValue} onChange={handleChange} onBlur={handleBlur} placeholder={placeholder} disabled={disabled} {...props} />;
}

// ── Badge Components ──
const Badge = ({ children, color, bg, border, title }) => (
  <span title={title} style={{
    fontSize: '11px', fontWeight: '600', color, background: bg,
    border: `1px solid ${border}`, borderRadius: '6px', padding: '2px 8px',
    whiteSpace: 'nowrap',
  }}>{children}</span>
);

const VerificationBadge = ({ status }) => {
  if (!status || status === 'not_required') return null;
  if (status === 'required') return <Badge color="#f59e0b" bg="#2a1a00" border="#92400e">✅ Verification Required</Badge>;
  if (status === 'optional') return <Badge color="#60a5fa" bg="#0f1f3d" border="#1d4ed8">☑️ Verification Optional</Badge>;
  return null;
};

// Can sellers (OnlyFans creators) post here at all? Never a blocker — a
// sellers-not-allowed sub is still worth posting in, just without promotion.
const SELLER_TEXT = { welcome: '💚 Sellers welcome', allowed: '💰 Sellers allowed', banned: '🚫 Sellers not allowed' };
const SellerBadge = ({ access }) => {
  if (access === 'welcome') return <Badge color="#22c55e" bg="#0a2a0a" border="#166534">{SELLER_TEXT.welcome}</Badge>;
  if (access === 'banned') return <Badge color="#f59e0b" bg="#2a1a00" border="#92400e">{SELLER_TEXT.banned}</Badge>;
  return null; // 'allowed' is the norm — no badge noise
};

// AI-generated content policy (from the sub's rules). Read from the rule
// tags so the list needs no extra query: ai-friendly / no-ai /
// ai-not-mentioned (the last only on subs the new scraper classified).
const AiBadge = ({ tags }) => {
  const t = new Set(tags || []);
  if (t.has('ai-friendly')) return <Badge color="#22c55e" bg="#0a2a0a" border="#166534">🤖 AI-friendly</Badge>;
  if (t.has('no-ai')) return <Badge color="#ef4444" bg="#2a0a0a" border="#7f1d1d">🚫 No AI</Badge>;
  if (t.has('ai-not-mentioned')) return <Badge color="#888" bg="#1a1a1a" border="#333">🤖 AI not mentioned</Badge>;
  return null;
};
// Shown as badges (or pure noise in the list) — not repeated as tag chips.
const LIST_HIDDEN_TAGS = new Set(['flair-required', 'ai-friendly', 'no-ai', 'ai-not-mentioned']);

// Where may the creator advertise herself (OF links, "check my profile")?
const PROMO_TEXT = { anywhere: '📣 Promo allowed', comments_only: '💬 Promo in comments only', approval: '🔕 Promo only with mod approval', nowhere: '🔕 No promotion' };
const PromoBadge = ({ policy }) => {
  if (!policy) return null;
  const color = policy === 'anywhere' ? ['#22c55e', '#0a2a0a', '#166534'] : policy === 'comments_only' ? ['#60a5fa', '#0a1a2a', '#1e3a5f'] : ['#aaa', '#1a1a1a', '#333'];
  return <Badge color={color[0]} bg={color[1]} border={color[2]}>{PROMO_TEXT[policy]}</Badge>;
};

const ScrapeStatusDot = ({ status, lastScraped }) => {
  const colors = {
    complete: '#22c55e', scraped: '#60a5fa', scraping: '#f59e0b',
    parsing: '#f59e0b', pending: '#555', failed: '#ef4444', discovered: '#6b7280',
  };
  const color = colors[status] || '#555';
  const title = status === 'complete' && lastScraped
    ? `Scraped: ${new Date(lastScraped).toLocaleDateString()}`
    : status === 'discovered' ? 'Added from the Competitors page — not scraped yet'
    : status || 'Not scraped';
  return <span title={title} style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />;
};

// ── Info Row (for expanded view) ──
const InfoRow = ({ label, value, icon }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: '8px' }}>
      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '2px' }}>{icon} {label}</label>
      <div style={{ fontSize: '13px', color: '#ccc', lineHeight: '1.4' }}>{value}</div>
    </div>
  );
};

function Subreddits({ role, viewingOrgId }) {
  const [subreddits, setSubreddits] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState(null);           // scraper queue + account health
  const [showScraperAccounts, setShowScraperAccounts] = useState(false);
  const [showFinder, setShowFinder] = useState(false);
  const [showFound, setShowFound] = useState(false);
  const [foundRefresh, setFoundRefresh] = useState(0); // the finder found more: reload Found
  const [rescraped, setRescraped] = useState({});     // ids queued this session
  const [showQueue, setShowQueue] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filterTags, setFilterTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [tags, setTags] = useState([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [sortBy, setSortBy] = useState('az');
  const [filterVerify, setFilterVerify] = useState('');
  const [filterSeller, setFilterSeller] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [filterRescraped, setFilterRescraped] = useState(false); // only subs the new scraper has processed
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(1);
  const [newForm, setNewForm] = useState({
    name: '', tags: '', rules: '', title_requirements: '',
    moderation_warning: '', bot_warning: '', general_info: '',
    posting_interval_hours: 0, size: 0
  });

  const searchTimerRef = useRef(null);
  const [searchInput, setSearchInput] = useState('');
  // Request counters: only the newest list / detail response is applied
  // (a slow search for "ab" must not replace the results for "abc").
  const listSeqRef = useRef(0);
  const expandSeqRef = useRef(0);
  const expandedIdRef = useRef(null);

  // Fetch subreddits from server with pagination
  const fetchSubreddits = useCallback(async (overrides = {}) => {
    const seq = ++listSeqRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', overrides.page || page);
      params.set('limit', overrides.perPage || perPage);
      params.set('sort', overrides.sort || sortBy);
      
      const s = overrides.search !== undefined ? overrides.search : search;
      if (s) params.set('search', s);
      
      const t = overrides.tags !== undefined ? overrides.tags : filterTags;
      if (t.length > 0) params.set('tags', t.join(','));
      
      const v = overrides.verify !== undefined ? overrides.verify : filterVerify;
      if (v) params.set('verification', v);
      
      const sel = overrides.seller !== undefined ? overrides.seller : filterSeller;
      if (sel) params.set('seller', sel);
      
      const r = overrides.risk !== undefined ? overrides.risk : filterRisk;
      if (r) params.set('promo', r);

      const rs = overrides.rescraped !== undefined ? overrides.rescraped : filterRescraped;
      if (rs) params.set('rescraped', '1');

      const res = await axios.get(`${API}/api/subreddits?${params.toString()}`);
      if (seq !== listSeqRef.current) return; // a newer request superseded this one
      setSubreddits(res.data.data);
      setTotalCount(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      if (seq !== listSeqRef.current) return;
      notifyError(err, 'Could not load subreddits');
    }
    setLoading(false);
  }, [page, perPage, sortBy, search, filterTags, filterVerify, filterSeller, filterRisk, filterRescraped]);

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/api/tags`);
      setTags(res.data);
      setAllTags(res.data.map(t => t.name).sort());
    } catch (err) {
      notifyError(err, 'Could not load tags');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/subreddits/stats`);
      setStats(res.data);
    } catch (err) { notifyError(err, 'Could not load subreddit stats'); }
  };

  // Scraper queue depth + account health — so the re-scrape can be watched
  // from the app instead of Railway logs.
  const fetchQueue = async () => {
    try {
      const res = await axios.get(`${API}/api/subreddits/scrape-queue`);
      setQueue(res.data);
    } catch (err) { /* endpoint may predate this deploy */ }
  };

  const rescrapeSub = async (id, name) => {
    try {
      await axios.post(`${API}/api/subreddits/${id}/rescrape`);
      setRescraped(prev => ({ ...prev, [id]: true }));
      fetchQueue();
    } catch (err) {
      alert('Could not queue re-scrape: ' + (err.response?.data?.error || err.message));
    }
  };

  useEffect(() => {
    fetchQueue();
    const qt = setInterval(fetchQueue, 15000); // live while a re-scrape runs
    return () => clearInterval(qt);
  }, []);

  useEffect(() => {
    fetchSubreddits();
    fetchTags();
    fetchStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when page/sort/filters change (but NOT search — that's debounced)
  useEffect(() => {
    fetchSubreddits();
  }, [page, perPage, sortBy, filterVerify, filterSeller, filterRisk, filterTags, filterRescraped]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  const handleSearchInput = (val) => {
    setSearchInput(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
      fetchSubreddits({ search: val, page: 1 });
    }, 400);
  };

  // Fetch full details when expanding
  const handleExpand = async (subId) => {
    const seq = ++expandSeqRef.current;
    if (expandedId === subId) {
      expandedIdRef.current = null;
      setExpandedId(null);
      setExpandedData(null);
      setExpandedLoading(false);
      return;
    }
    expandedIdRef.current = subId;
    setExpandedId(subId);
    setExpandedData(null);
    setExpandedLoading(true);
    try {
      const res = await axios.get(`${API}/api/subreddits/${subId}`);
      // Late response for a sub that's no longer open → drop it, or the
      // panel (and its auto-save fields) would show/save the wrong sub.
      if (seq !== expandSeqRef.current || expandedIdRef.current !== subId) return;
      setExpandedData(res.data);
    } catch (err) {
      if (seq !== expandSeqRef.current) return;
      notifyError(err, 'Could not load subreddit details');
    }
    setExpandedLoading(false);
  };

  const addTag = async () => {
    if (!newTagName.trim()) return;
    try {
      await axios.post(`${API}/api/tags`, { name: newTagName });
      setNewTagName('');
      fetchTags();
    } catch (err) {
      notifyError(err, 'Could not add tag');
    }
  };

  const deleteTag = async (id) => {
    try {
      await axios.delete(`${API}/api/tags/${id}`);
      fetchTags();
    } catch (err) {
      notifyError(err, 'Could not delete tag');
    }
  };

  const handleAdd = async () => {
    if (!newForm.name) return;
    setLoading(true);
    const payload = {
      ...newForm,
      name: newForm.name.startsWith('r/') ? newForm.name : 'r/' + newForm.name,
      tags: newForm.tags ? newForm.tags.split(',').map(t => t.trim()) : [],
      posting_interval_hours: parseInt(newForm.posting_interval_hours) || 0,
      size: parseInt(newForm.size) || 0
    };
    try {
      await axios.post(`${API}/api/subreddits`, payload);
      setNewForm({ name: '', tags: '', rules: '', title_requirements: '', moderation_warning: '', bot_warning: '', general_info: '', posting_interval_hours: 0, size: 0 });
      setShowForm(false);
      fetchSubreddits();
    } catch (err) {
      notifyError(err, 'Could not add subreddit');
    }
    setLoading(false);
  };

  const updateField = async (id, field, value) => {
    try {
      await axios.put(`${API}/api/subreddits/${id}`, { [field]: value });
      // Update expanded data if open (and it's still this sub)
      setExpandedData(prev => (prev && prev.id === id ? { ...prev, [field]: value } : prev));
      // Update list item
      setSubreddits(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
    } catch (err) {
      notifyError(err, 'Could not save');
    }
  };

  const toggleTag = async (subId, tagName) => {
    const sub = (expandedData && expandedData.id === subId ? expandedData : null) || subreddits.find(s => s.id === subId);
    const current = sub?.tags || [];
    const updated = current.includes(tagName) ? current.filter(t => t !== tagName) : [...current, tagName];
    await updateField(subId, 'tags', updated);
  };

  const handleBulkAdd = async () => {
    if (!bulkText.trim()) return;
    setBulkLoading(true);
    setBulkResults(null);
    try {
      const res = await axios.post(`${API}/api/subreddits/bulk-ensure`, {
        names: bulkText.split(/[\n,]+/).map(l => l.trim()).filter(Boolean)
      });
      setBulkResults({
        added: res.data.created,
        total: res.data.subreddits.length
      });
      fetchSubreddits();
    } catch (err) {
      notifyError(err, 'Bulk add failed');
    }
    setBulkLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subreddit?')) return;
    try {
      await axios.delete(`${API}/api/subreddits/${id}`);
      setSubreddits(prev => prev.filter(s => s.id !== id));
      if (expandedId === id) { expandedIdRef.current = null; expandSeqRef.current++; setExpandedId(null); setExpandedData(null); }
    } catch (err) {
      notifyError(err, 'Could not delete subreddit');
    }
  };

  const renderTagSelector = (currentTags, onToggle) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
      {tags.map(tag => {
        const selected = currentTags.includes(tag.name);
        return (
          <span key={tag.id} onClick={() => role !== 'poster_va' && onToggle(tag.name)} style={{
            padding: '4px 12px', borderRadius: '20px', fontSize: '12px',
            cursor: role === 'poster_va' ? 'default' : 'pointer',
            background: selected ? '#ff4500' : '#2a2a2a',
            color: selected ? '#ffffff' : '#aaaaaa',
            border: selected ? '1px solid #ff4500' : '1px solid #444',
            userSelect: 'none'
          }}>{tag.name}</span>
        );
      })}
    </div>
  );

  const renderNewFormTagSelector = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
      {tags.map(tag => {
        const current = newForm.tags.split(',').map(t => t.trim()).filter(Boolean);
        const selected = current.includes(tag.name);
        return (
          <span key={tag.id} onClick={() => {
            const updated = selected ? current.filter(t => t !== tag.name) : [...current, tag.name];
            setNewForm({ ...newForm, tags: updated.join(', ') });
          }} style={{
            padding: '4px 12px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer',
            background: selected ? '#ff4500' : '#2a2a2a',
            color: selected ? '#ffffff' : '#aaaaaa',
            border: selected ? '1px solid #ff4500' : '1px solid #444',
            userSelect: 'none'
          }}>{tag.name}</span>
        );
      })}
    </div>
  );

  // Use expandedData for the detail view, fall back to list data

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Subreddits</h1>
          <p>Your subreddit library — {stats ? `${stats.total} subreddits (${stats.scraped} scraped, ${stats.dead} dead, ${stats.pending} pending)` : `${totalCount} subreddits`}</p>
          {queue && (
            <div style={{ marginTop: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span
                onClick={() => setShowQueue(v => !v)}
                title="Scraper queue — click for account health"
                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#aaa' }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: queue.scraping > 0 ? '#f59e0b' : queue.pending > 0 ? '#60a5fa' : '#22c55e'
                }} />
                Scraper: <strong style={{ color: '#eee' }}>{queue.scraping}</strong> running ·{' '}
                <strong style={{ color: '#eee' }}>{queue.pending}</strong> queued
                {queue.failed > 0 && <span style={{ color: '#ef4444' }}>· {queue.failed} failed</span>}
                {queue.stale > 0 && <span style={{ color: '#666' }}>· {queue.stale} stale</span>}
                <span style={{ color: '#555' }}>{showQueue ? '▲' : '▼'}</span>
              </span>
            </div>
          )}
          {showQueue && queue && (
            <div style={{ marginTop: '8px', background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '10px', maxWidth: '520px' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scraper accounts</div>
              {(queue.accounts || []).length === 0 ? (
                <div style={{ fontSize: '12px', color: '#666' }}>No scraper accounts configured yet.</div>
              ) : queue.accounts.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '3px 0' }}>
                  <span style={{
                    width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
                    background: a.status === 'active' ? (a.fail_count > 0 ? '#f59e0b' : '#22c55e') : '#ef4444'
                  }} />
                  <span style={{ color: '#ccc', minWidth: '130px' }}>{a.username}</span>
                  <span style={{ color: '#666' }}>{a.status}</span>
                  {a.fail_count > 0 && <span style={{ color: '#f59e0b' }}>{a.fail_count} fails</span>}
                  {a.last_error && <span title={a.last_error} style={{ color: '#ef4444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>{a.last_error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {(role === 'admin' || role === 'super_admin') && (
            <button
              style={{ background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
              onClick={() => setShowScraperAccounts(v => !v)}
            >
              {showScraperAccounts ? 'Hide scraper accounts' : 'Scraper accounts'}
            </button>
          )}
          {role !== 'poster_va' && (
            <>
              <button
                style={{ background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                onClick={() => setShowFound(v => !v)}
                title="Subs the finder or the Competitors page added that nobody has scraped yet"
              >
                {showFound ? 'Hide found' : 'Found (not scraped)'}
              </button>
              <button
                style={{ background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                onClick={() => setShowFinder(v => !v)}
              >
                {showFinder ? 'Hide finder' : '🔎 Find subreddits'}
              </button>
              <button
                style={{ background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                onClick={() => { setShowBulkForm(!showBulkForm); setBulkResults(null); }}
              >
                {showBulkForm ? 'Cancel Bulk' : '+ Bulk Add'}
              </button>
              <button onClick={() => setShowForm(!showForm)}>
                {showForm ? 'Cancel' : '+ Add Subreddit'}
              </button>
            </>
          )}
        </div>
      </div>

      {showScraperAccounts && <ScraperAccounts />}
      {showFinder && <SubFinder viewingOrgId={viewingOrgId} onClose={() => setShowFinder(false)} onFound={() => setFoundRefresh(n => n + 1)} />}
      {showFound && <FoundSubs refreshKey={foundRefresh} />}

      {/* Tag Library */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Tag Library</h2>
          {role !== 'poster_va' && (
            <button
              style={{ background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' }}
              onClick={() => setShowTagManager(!showTagManager)}
            >
              {showTagManager ? 'Hide' : 'Manage Tags'}
            </button>
          )}
        </div>
        {showTagManager && (
          <div style={{ marginTop: '16px' }}>
            <div className="form-row" style={{ marginBottom: '16px' }}>
              <input placeholder="New tag name" value={newTagName} onChange={e => setNewTagName(e.target.value)} onKeyPress={e => { if (e.key === 'Enter') addTag(); }} />
              <button onClick={addTag}>Add Tag</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {[...tags].sort((a, b) => a.name.localeCompare(b.name)).map(tag => (
                <span key={tag.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#2a2a2a', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', color: '#aaaaaa' }}>
                  {tag.name}
                  <span onClick={() => deleteTag(tag.id)} style={{ cursor: 'pointer', color: '#ff4500', fontWeight: '700', fontSize: '14px', lineHeight: 1 }}>×</span>
                </span>
              ))}
              {tags.length === 0 && <p className="empty">No tags yet.</p>}
            </div>
          </div>
        )}
      </div>

      {/* Bulk Add */}
      {showBulkForm && (
        <div className="card">
          <h2>Bulk Add Subreddits</h2>
          <p style={{ color: '#aaaaaa', fontSize: '13px', marginBottom: '12px' }}>
            Paste subreddit names or links, one per line or comma separated.
          </p>
          <textarea placeholder="r/funny" value={bulkText} onChange={e => setBulkText(e.target.value)} rows={18} style={{ width: '100%' }} />
          <div className="form-actions">
            <button onClick={handleBulkAdd} disabled={bulkLoading}>{bulkLoading ? 'Adding...' : 'Add All'}</button>
          </div>
          {bulkResults && (
            <div style={{ marginTop: '16px', fontSize: '13px', color: '#22c55e' }}>
              ✅ {bulkResults.total} subreddits processed ({bulkResults.added} new)
            </div>
          )}
        </div>
      )}

      {/* Add Subreddit Form */}
      {showForm && (
        <div className="card">
          <h2>Add Subreddit</h2>
          <div className="form-grid">
            <input placeholder="Subreddit name (e.g. funny)" value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })} />
          </div>
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="field-group">
              <label>Tags</label>
              {renderNewFormTagSelector()}
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div className="field-group" style={{ flex: 1, minWidth: '120px' }}>
                <label>Posting interval (hours)</label>
                <input type="number" min="0" value={newForm.posting_interval_hours} onChange={e => setNewForm({ ...newForm, posting_interval_hours: e.target.value })} />
              </div>
              <div className="field-group" style={{ flex: 1, minWidth: '120px' }}>
                <label>Members</label>
                <input type="number" min="0" placeholder="e.g. 1500000" value={newForm.size || ''} onChange={e => setNewForm({ ...newForm, size: e.target.value })} />
              </div>
            </div>
            <div className="field-group"><label>📝 Title requirements</label><input placeholder="e.g. must include username" value={newForm.title_requirements} onChange={e => setNewForm({ ...newForm, title_requirements: e.target.value })} /></div>
            <div className="field-group"><label>🗣️ Moderation warning</label><input placeholder="e.g. strict manual review" value={newForm.moderation_warning} onChange={e => setNewForm({ ...newForm, moderation_warning: e.target.value })} /></div>
            <div className="field-group"><label>🤖 Bot warning</label><input placeholder="e.g. auto-removes links" value={newForm.bot_warning} onChange={e => setNewForm({ ...newForm, bot_warning: e.target.value })} /></div>
            <div className="field-group"><label>📋 Rules</label><textarea placeholder="Subreddit rules" value={newForm.rules} onChange={e => setNewForm({ ...newForm, rules: e.target.value })} rows={3} style={{ width: '100%' }} /></div>
            <div className="field-group"><label>ℹ️ General info</label><textarea placeholder="What is this subreddit about..." value={newForm.general_info} onChange={e => setNewForm({ ...newForm, general_info: e.target.value })} rows={3} style={{ width: '100%' }} /></div>
          </div>
          <div className="form-actions" style={{ marginTop: '16px' }}>
            <button onClick={handleAdd} disabled={loading}>{loading ? 'Saving...' : 'Add Subreddit'}</button>
          </div>
        </div>
      )}

      {/* Subreddit List */}
      <div className="card">
        <div className="filter-row">
          <input
            placeholder="Search subreddits..."
            value={searchInput}
            onChange={e => handleSearchInput(e.target.value)}
          />
          <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}>
            <option value="az">A → Z</option>
            <option value="za">Z → A</option>
            <option value="newest">Newest</option>
            <option value="size_desc">Size ↓</option>
            <option value="size_asc">Size ↑</option>
            <option value="visitors_desc">Visitors ↓</option>
            <option value="scraped">Scraped First</option>
            <option value="recent_scrape">Recently scraped</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#aaa', cursor: 'pointer', whiteSpace: 'nowrap' }}
            title="Only subreddits processed by the new scraper — the rest still hold old data until the full run reaches them">
            <input type="checkbox" checked={filterRescraped}
              onChange={e => { setFilterRescraped(e.target.checked); setPage(1); }} />
            Re-scraped only
          </label>
          <select value={filterVerify} onChange={e => { setFilterVerify(e.target.value); setPage(1); }}>
            <option value="">All Verification</option>
            <option value="required">✅ Required</option>
            <option value="optional">☑️ Optional</option>
            <option value="not_required">❌ Not Required</option>
          </select>
          <select value={filterSeller} onChange={e => { setFilterSeller(e.target.value); setPage(1); }}>
            <option value="">All sellers</option>
            <option value="welcome">💚 Sellers welcome</option>
            <option value="allowed">💰 Sellers allowed</option>
            <option value="banned">🚫 Sellers not allowed</option>
          </select>
          <select value={filterRisk} onChange={e => { setFilterRisk(e.target.value); setPage(1); }}>
            <option value="">All promotion</option>
            <option value="anywhere">📣 Promo allowed</option>
            <option value="comments_only">💬 Promo in comments only</option>
            <option value="nowhere">🔕 No promotion</option>
          </select>
          <select value={perPage} onChange={e => { setPerPage(parseInt(e.target.value)); setPage(1); }}>
            {[20, 50, 100].map(n => (
              <option key={n} value={n}>{n} per page</option>
            ))}
          </select>
        </div>

        <TagFilter tags={allTags} selected={filterTags} onChange={next => { setFilterTags(next); setPage(1); }} />

        {/* Loading indicator */}
        {loading && <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Loading...</div>}

        {/* Results info */}
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
          Showing {subreddits.length} of {totalCount} subreddits (page {page} of {totalPages})
        </div>

        {!loading && subreddits.length === 0 ? (
          <p className="empty">No subreddits found.</p>
        ) : (
          <div className="list">
            {subreddits.map(sub => (
              <div key={sub.id}>
                <div className="list-item">
                  <div className="list-item-main">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ScrapeStatusDot status={sub.scrape_status} lastScraped={sub.last_scraped} />
                      <a href={'https://www.reddit.com/' + sub.name} target="_blank" rel="noreferrer" style={{ color: '#ff4500', textDecoration: 'none', fontWeight: '700', fontSize: '14px' }}>
                        {sub.name}
                      </a>
                      {sub.is_nsfw && <span style={{ fontSize: '10px', color: '#ef4444', background: '#2a0a0a', border: '1px solid #7f1d1d', borderRadius: '4px', padding: '1px 5px', fontWeight: '700' }}>NSFW</span>}
                      {sub.weekly_visitors && <span style={{ fontSize: '12px', color: '#888' }}>👀 {sub.weekly_visitors} weekly</span>}
                    </div>
                    <div className="tags" style={{ marginTop: '4px' }}>
                      {(sub.tags || []).filter(tag => !LIST_HIDDEN_TAGS.has(tag)).map(tag => <span className="tag" key={tag}>{tag}</span>)}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                      <VerificationBadge status={sub.verification_status} />
                      <SellerBadge access={sub.seller_access} />
                      <PromoBadge policy={sub.promo_policy} />
                      {sub.has_bouncer_bot && <Badge color="#ef4444" bg="#2a0a0a" border="#7f1d1d">🛡️ Bouncer Bot</Badge>}
                      {sub.has_repost_bot && <Badge color="#f59e0b" bg="#2a1a00" border="#92400e">👁️ Repost Bot</Badge>}
                      <AiBadge tags={sub.tags} />
                      {sub.restricted && <Badge color="#a78bfa" bg="#1a1030" border="#4c1d95" title="Only approved users can post here; everyone else gets Reddit's “Request to Post”. An account needs the mods' approval first.">✋ Request to Post</Badge>}
                      {sub.allows_images === false && <Badge color="#ef4444" bg="#2a0a0a" border="#7f1d1d">🚫 No image posts</Badge>}
                      {sub.allows_galleries === false && sub.allows_images !== false && <Badge color="#888" bg="#1a1a1a" border="#333">No galleries</Badge>}
                      {sub.scraped_at && (
                        <Badge color="#22c55e" bg="#0a1f0a" border="#166534">
                          ✓ Re-scraped {new Date(sub.scraped_at).toLocaleDateString()}
                        </Badge>
                      )}
                      {sub.posting_interval_hours > 0 && <span style={{ fontSize: '11px', color: '#888' }}>⏱ {sub.posting_interval_hours}h</span>}
                      {sub.content_cooldown_days > 0 && sub.content_cooldown_days !== 30 && (
                        <span title="Content cooldown — the same item can't be reposted here until this many days pass"
                          style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '8px', background: '#2a1a00', color: '#f59e0b', border: '1px solid #92400e' }}>
                          🔁 {sub.content_cooldown_days}d
                        </span>
                      )}
                    </div>
                    {(sub.title_requirements || sub.moderation_warning || sub.bot_warning) && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                        {sub.title_requirements && (
                          <span style={{ fontSize: '11px', color: '#f59e0b', background: '#2a1a00', border: '1px solid #92400e', borderRadius: '6px', padding: '2px 8px' }}>
                            📝 {sub.title_requirements}
                          </span>
                        )}
                        {sub.moderation_warning && (
                          <span style={{ fontSize: '11px', color: '#3b82f6', background: '#0f1f3d', border: '1px solid #1d4ed8', borderRadius: '6px', padding: '2px 8px' }}>
                            🗣️ {sub.moderation_warning}
                          </span>
                        )}
                        {sub.bot_warning && (
                          <span style={{ fontSize: '11px', color: '#ef4444', background: '#2a0a0a', border: '1px solid #7f1d1d', borderRadius: '6px', padding: '2px 8px' }}>
                            🤖 {sub.bot_warning}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="list-item-actions">
                    {(!sub.scrape_status || sub.scrape_status === 'pending') && (
                      <span title="Not scraped yet" style={{ color: '#f59e0b', fontSize: '16px', cursor: 'help', alignSelf: 'center' }}>⚠️</span>
                    )}
                    {sub.scrape_status === 'discovered' && role !== 'poster_va' && (
                      <button className="btn-secondary" disabled={!!rescraped[sub.id]} onClick={() => rescrapeSub(sub.id, sub.name)}
                        title="Added from the Competitors page and not scraped yet: rules, flairs and weekly visitors come with a scrape (about $0.02)">
                        {rescraped[sub.id] ? 'Queued' : 'Scrape'}
                      </button>
                    )}
                    <button className="btn-secondary" onClick={() => handleExpand(sub.id)}>
                      {expandedId === sub.id ? 'Collapse' : role === 'poster_va' ? 'View' : 'Edit'}
                    </button>
                    {role !== 'poster_va' && (
                      <button className="btn-danger" onClick={() => handleDelete(sub.id)}>Delete</button>
                    )}
                  </div>
                </div>

                {expandedId === sub.id && (
                  <div style={{ background: '#111', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

                    {/* Loading state */}
                    {expandedLoading && <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Loading details...</div>}

                    {/* ── Scraped Intelligence (from full detail endpoint) ── */}
                    {expandedData && expandedData.scrape_status === 'complete' && (
                      <div style={{ background: '#0a0a0a', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: '#ff4500', textTransform: 'uppercase', letterSpacing: '1px' }}>📡 Scraped Intelligence</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '10px', color: '#555' }}>Last scraped: {expandedData.scraped_at || expandedData.last_scraped ? new Date(expandedData.scraped_at || expandedData.last_scraped).toLocaleDateString() : '—'}</span>
                            {role !== 'poster_va' && (
                              <button
                                onClick={() => rescrapeSub(sub.id, sub.name)}
                                disabled={!!rescraped[sub.id]}
                                title="Queue this subreddit for an immediate re-scrape"
                                style={{
                                  fontSize: '10px', padding: '3px 9px', borderRadius: '6px', cursor: rescraped[sub.id] ? 'default' : 'pointer',
                                  background: rescraped[sub.id] ? '#0a2a0a' : 'transparent',
                                  color: rescraped[sub.id] ? '#22c55e' : '#aaa',
                                  border: `1px solid ${rescraped[sub.id] ? '#166534' : '#444'}`
                                }}>
                                {rescraped[sub.id] ? '✓ Queued' : '🔄 Re-scrape'}
                              </button>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                          <InfoRow icon="👀" label="WEEKLY VISITORS" value={expandedData.weekly_visitors} />
                          <InfoRow icon="📊" label="WEEKLY CONTRIBUTIONS" value={expandedData.weekly_contributions} />
                          <InfoRow icon="✅" label="VERIFICATION" value={expandedData.verification_status === 'required' ? '🔴 Required' : expandedData.verification_status === 'optional' ? '🟡 Optional' : expandedData.verification_status === 'not_required' ? '🟢 Not Required' : '— not stated in rules'} />
                          <InfoRow icon="💰" label="SELLERS" value={SELLER_TEXT[expandedData.seller_access] || (expandedData.scraped_at ? '— rules don\'t say' : null)} />
                          <InfoRow icon="📣" label="IN-POST PROMOTION" value={PROMO_TEXT[expandedData.promo_policy] || (expandedData.scraped_at ? '— rules don\'t say' : null)} />
                          <InfoRow icon="⏱" label="REPOST INTERVAL" value={expandedData.repost_interval_days ? `${expandedData.repost_interval_days} days` : null} />
                        </div>

                        {expandedData.scraped_at && (() => {
                          const d = expandedData;
                          const yn = (v) => v === true ? '✅ yes' : v === false ? '❌ no' : '—';
                          const list = (v) => Array.isArray(v) && v.length ? v.join(' · ') : null;
                          const mods = Array.isArray(d.raw_moderators) ? d.raw_moderators : [];
                          const ev = d.classification_evidence && typeof d.classification_evidence === 'object' ? Object.entries(d.classification_evidence) : [];
                          return (
                            <div style={{ background: '#0f0f0f', border: '1px solid #1f2937', borderRadius: '8px', padding: '10px 12px', margin: '10px 0' }}>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#60a5fa', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                📐 POSTING FACTS — straight from Reddit
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '6px 16px', fontSize: '12px', color: '#ccc', marginBottom: '8px' }}>
                                <div>Image posts: {yn(d.allows_images)}</div>
                                <div>Galleries: {yn(d.allows_galleries)}</div>
                                <div>Videos: {yn(d.allows_videos)}</div>
                                <div>Post types: {d.submission_type === 'any' ? 'links + text' : d.submission_type === 'link' ? 'links/media only' : d.submission_type === 'self' ? 'text only' : '—'}</div>
                                <div>Creator posts: {d.accepts_creator_content === false ? '⚠ not for creators' : d.accepts_creator_content === true ? '✅ yes' : '—'}</div>
                                <div>Who can post: {d.subreddit_type === 'restricted' ? '✋ approved users only' : d.subreddit_type === 'public' ? '✅ everyone' : d.subreddit_type || '—'}</div>
                                <div>Flair required: {yn(d.flair_required)}{d.flair_required && Array.isArray(d.flairs) && !d.flairs.length ? ' (Reddit says so, but the post form offers none)' : ''}</div>
                                <div>AI content: {d.ai_policy === 'friendly' ? '🤖 allowed' : d.ai_policy === 'banned' ? '🚫 banned' : d.scraped_at ? 'not mentioned' : '—'}</div>
                                <div>Flairs known: {Array.isArray(d.flairs) ? d.flairs.length : d.subreddit_type === 'restricted' ? 'only shown to approved posters' : 'not collected yet'}</div>
                                <div>Members: {d.size ? Number(d.size).toLocaleString() : '—'}</div>
                                <div>Created: {d.subreddit_created_at ? new Date(d.subreddit_created_at).toLocaleDateString() : '—'}</div>
                              </div>
                              {d.accepts_creator_content === false && (
                                <div style={{ background: '#2a1a0a', border: '1px solid #92400e', color: '#fbbf24', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', marginBottom: '8px' }}>
                                  ⚠ Not for creator posts — {d.creator_content_reason || 'the sub is for something else.'} Still suggested and scheduled; post as a regular user, never as a creator, and no promotion.
                                </div>
                              )}
                              <InfoRow icon="🎮" label="FRANCHISES" value={list(d.franchises)} />
                              <InfoRow icon="🧝" label="CHARACTERS" value={list(d.characters)} />
                              <InfoRow icon="🔤" label="TITLE MUST CONTAIN ONE OF" value={list(d.title_required_strings)} />
                              <InfoRow icon="⛔" label="TITLE MUST NOT CONTAIN" value={list(d.title_blacklisted_strings)} />
                              <InfoRow icon="🧩" label="TITLE FORMAT (regex Reddit enforces)" value={list(d.title_regexes)} />
                              <InfoRow icon="📏" label="TITLE LENGTH" value={d.title_min_length || d.title_max_length ? `${d.title_min_length || 0}–${d.title_max_length || 300} characters` : null} />
                              <InfoRow icon="🔗" label="ALLOWED LINK DOMAINS" value={list(d.domain_whitelist)} />
                              <InfoRow icon="🛡" label={`MODERATORS (${mods.length})`} value={mods.length ? mods.map(m => (m.name || m) + (m.is_bot ? ' 🤖' : '')).join(', ') : null} />
                              {ev.length > 0 && (
                                <details style={{ marginTop: '4px' }}>
                                  <summary style={{ fontSize: '11px', color: '#888', cursor: 'pointer' }}>
                                    Evidence — the exact rule text behind each AI answer ({ev.length})
                                  </summary>
                                  <div style={{ marginTop: '6px' }}>
                                    {ev.map(([k, q]) => (
                                      <div key={k} style={{ fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>
                                        <span style={{ color: '#60a5fa' }}>{k}</span>: “{String(q)}”
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                              <div style={{ fontSize: '10px', color: '#555', marginTop: '6px' }}>
                                Scraped {new Date(d.scraped_at).toLocaleString()}{d.classified_model ? ` · classified by ${d.classified_model}` : ''}
                              </div>
                            </div>
                          );
                        })()}

                        {expandedData.verification_instructions && (
                          <InfoRow icon="📋" label="VERIFICATION INSTRUCTIONS" value={expandedData.verification_instructions} />
                        )}
                        <InfoRow icon="📢" label="SELF-PROMOTION RULES" value={expandedData.self_promotion_rules} />
                        <InfoRow icon="🏷️" label="WATERMARK RULES" value={expandedData.watermark_rules} />
                        <InfoRow icon="💬" label="COMMENTING RULES" value={expandedData.commenting_rules} />
                        <InfoRow icon="🚫" label="CONTENT RESTRICTIONS" value={expandedData.content_restrictions} />
                        <InfoRow icon="✅" label="CONTENT REQUIREMENTS" value={expandedData.content_requirements} />
                        <InfoRow icon="🎨" label="AI / EDITING RULES" value={expandedData.ai_content_rules} />
                        <InfoRow icon="👤" label="GENDER RULES" value={expandedData.gender_rules} />
                        <InfoRow icon="🔄" label="CROSSPOST RULES" value={expandedData.crosspost_rules} />
                        <InfoRow icon="⭐" label="KARMA REQUIREMENTS" value={expandedData.karma_requirements} />
                        <InfoRow icon="📅" label="ACCOUNT AGE REQUIREMENTS" value={expandedData.account_age_requirements} />
                        <InfoRow icon="📆" label="POSTING DAYS" value={expandedData.posting_days} />
                      </div>
                    )}

                    {/* ── Editable Fields ── */}
                    {expandedData && (
                      <>
                        <div>
                          <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>TAGS</label>
                          {renderTagSelector(expandedData.tags || [], (tagName) => toggleTag(sub.id, tagName))}
                        </div>

                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>POSTING INTERVAL (hours)</label>
                            <AutoSaveInput value={expandedData.posting_interval_hours || ''} onSave={val => updateField(sub.id, 'posting_interval_hours', parseInt(val) || 0)} placeholder="0" disabled={role === 'poster_va'} type="number" min="0" />
                          </div>
                          <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>MEMBERS</label>
                            <AutoSaveInput value={expandedData.size || ''} onSave={val => updateField(sub.id, 'size', parseInt(val) || 0)} placeholder="e.g. 1500000" disabled={role === 'poster_va'} type="number" min="0" />
                          </div>
                          <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }} title="Days before the same content may be reposted here. Blank = 30.">CONTENT COOLDOWN (days)</label>
                            <AutoSaveInput value={expandedData.content_cooldown_days || ''} onSave={val => updateField(sub.id, 'content_cooldown_days', val === '' ? null : (parseInt(val) || null))} placeholder="30 (default)" disabled={role === 'poster_va'} type="number" min="0" />
                          </div>
                        </div>

                        {/* Post flair — required by many subs, so the extension
                            needs a stored choice to post unattended. The list is
                            harvested automatically the first time the extension
                            opens this sub's flair picker. */}
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '160px' }}>
                            <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>POST FLAIR</label>
                            {Array.isArray(expandedData.flairs) && expandedData.flairs.length > 0 ? (
                              <select
                                value={expandedData.default_flair_id || ''}
                                disabled={role === 'poster_va'}
                                onChange={e => updateField(sub.id, 'default_flair_id', e.target.value || null)}
                                style={{ width: '100%', padding: '7px', background: '#0f0f0f', color: '#eee', border: '1px solid #333', borderRadius: '6px', fontSize: '12px' }}>
                                <option value="">— none —</option>
                                {expandedData.flairs.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                              </select>
                            ) : (
                              <div style={{ fontSize: '11px', color: '#666', padding: '7px 0' }}>
                                No flairs known yet — auto-filled after the extension opens this sub's flair picker once.
                              </div>
                            )}
                          </div>
                          {Array.isArray(expandedData.flairs) && expandedData.flairs.length > 0 && (
                            <div style={{ flex: 1, minWidth: '160px' }}>
                              <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }} title="Used instead of the default when the post is a GIF.">GIF FLAIR (optional)</label>
                              <select
                                value={expandedData.gif_flair_id || ''}
                                disabled={role === 'poster_va'}
                                onChange={e => updateField(sub.id, 'gif_flair_id', e.target.value || null)}
                                style={{ width: '100%', padding: '7px', background: '#0f0f0f', color: '#eee', border: '1px solid #333', borderRadius: '6px', fontSize: '12px' }}>
                                <option value="">— same as default —</option>
                                {expandedData.flairs.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                              </select>
                            </div>
                          )}
                        </div>

                        <div>
                          <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>📝 TITLE REQUIREMENTS</label>
                          <AutoSaveInput value={expandedData.title_requirements || ''} onSave={val => updateField(sub.id, 'title_requirements', val)} placeholder="e.g. must include username" disabled={role === 'poster_va'} />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>🗣️ MODERATION WARNING</label>
                          <AutoSaveInput value={expandedData.moderation_warning || ''} onSave={val => updateField(sub.id, 'moderation_warning', val)} placeholder="e.g. strict manual review" disabled={role === 'poster_va'} />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>🤖 BOT WARNING</label>
                          <AutoSaveInput value={expandedData.bot_warning || ''} onSave={val => updateField(sub.id, 'bot_warning', val)} placeholder="e.g. auto-removes links" disabled={role === 'poster_va'} />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>📋 RULES</label>
                          <AutoSaveInput value={expandedData.rules || ''} onSave={val => updateField(sub.id, 'rules', val)} placeholder="Subreddit rules..." disabled={role === 'poster_va'} multiline />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>ℹ️ GENERAL INFO</label>
                          <AutoSaveInput value={expandedData.general_info || ''} onSave={val => updateField(sub.id, 'general_info', val)} placeholder="What is this subreddit about..." disabled={role === 'poster_va'} multiline />
                        </div>

                        <div style={{ fontSize: '11px', color: '#555', textAlign: 'right' }}>
                          Changes save automatically
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
            <button style={{ padding: '6px 14px', background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', cursor: 'pointer' }} disabled={page === 1} onClick={() => setPage(page - 1)}>← Prev</button>
            <span style={{ color: '#aaaaaa', fontSize: '13px' }}>{page} of {totalPages}</span>
            <button style={{ padding: '6px 14px', background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', cursor: 'pointer' }} disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Subreddits;